/**
 * Raporti i pagave — faqosja e Excel-it dhe e PDF-së.
 *
 * Kolonat grupohen si te «Lista e Pagave»: Punonjësi · Të ardhurat ·
 * Kontributet · Ndalesat · NETO. Trembëdhjetë kolona të barabarta janë mur
 * shifrash; blloqet e tregojnë strukturën para se të lexohen numrat, dhe
 * rreshti mbyllet aty ku duhet — bruto minus kontributi, tatimi, sigurimi,
 * avanci dhe ndalesat japin NETO-n.
 *
 * Ky raport NUK është regjistri zyrtar (`payrollRegisterKS`): aty tatimi
 * rillogaritet sipas brezave në fuqi për dorëzim, kurse këtu shfaqen shumat e
 * ruajtura te lista. Prandaj janë dy dokumente dhe jo një.
 */
import { exportSheetExcel, downloadSheetPdf, printSheet } from './reportSheetExport';

export function payrollLayout(rows, { company = {}, period = '', status = '', t, fileBase = 'raporti-i-pagave' } = {}) {
  const sum = (k) => (rows || []).reduce((a, r) => a + (Number(r[k]) || 0), 0);
  const title = t('reports.payroll');

  const columns = [
    { key: 'no',       label: t('report.nr'),              w:  5, align: 'center', pdfW: 8 },
    { key: 'name',     label: t('report.worker'),          w: 26 },
    { key: 'nid',      label: t('report.personalNr'),      w: 15 },
    { key: 'position', label: t('report.position'),        w: 18, muted: true },
    { key: 'dept',     label: t('report.department'),      w: 16, muted: true },
    { key: 'base',     label: t('report.baseSalary'),      w: 12, money: true },
    { key: 'overtime', label: t('report.overtime'),        w: 11, money: true },
    { key: 'bonus',    label: t('report.bonus'),           w: 11, money: true },
    { key: 'gross',    label: t('report.gross'),           w: 12, money: true, bold: true },
    { key: 'pensEmp',  label: t('report.pensionWorker'),   w: 13, money: true },
    { key: 'pensEr',   label: t('report.pensionEmployer'), w: 13, money: true },
    { key: 'tax',      label: t('report.tax'),             w: 11, money: true },
    { key: 'health',   label: t('report.healthIns'),       w: 11, money: true },
    { key: 'advance',  label: t('report.advancements'),    w: 11, money: true },
    { key: 'penalty',  label: t('report.penalties'),       w: 11, money: true },
    { key: 'net',      label: t('report.net'),             w: 13, money: true, bold: true },
  ];

  const groups = [
    { label: '',                       keys: ['no', 'name', 'nid', 'position', 'dept'] },
    { label: t('report.earnings'),     keys: ['base', 'overtime', 'bonus', 'gross'] },
    { label: t('report.contributions'), keys: ['pensEmp', 'pensEr'] },
    { label: t('report.deductions'),   keys: ['tax', 'health', 'advance', 'penalty'] },
    { label: '',                       keys: ['net'] },
  ];

  const totals = Object.fromEntries(
    columns.filter((c) => c.money).map((c) => [c.key, sum(c.key)])
  );

  const gross = totals.gross || 0;
  // Kostoja e vërtetë e listës: bruto plus kontributi i punëdhënësit. Kjo shifër
  // nuk del askund tjetër, ndonëse është ajo që del nga arka.
  const employerCost = gross + (totals.pensEr || 0);

  return {
    title: title.toUpperCase(),
    logo: company.Logo || null,
    left: [
      company.CompanyName || company.name || '—',
      [company.NUI && `NUI: ${company.NUI}`, company.FiscalNumber && `NRF: ${company.FiscalNumber}`].filter(Boolean).join('   ·   '),
      company.Address || '',
      company.Phone ? `Tel: ${company.Phone}` : '',
    ],
    right: [
      `${t('report.period')}: ${period}`,
      `${t('report.workers')}: ${rows.length}`,
      status ? `${t('report.listStatus')}: ${status}` : '',
      company.BankName ? `${t('report.bank')}: ${company.BankName}` : '',
    ],
    columns,
    groups,
    body: rows,
    totals,
    totalLabel: t('report.totalRow'),
    summaryTitle: t('report.summaryTitle'),
    summary: [
      { label: t('report.gross'),        value: gross, money: true },
      { label: t('report.deductions'),   value: (totals.pensEmp || 0) + (totals.tax || 0) + (totals.health || 0) + (totals.advance || 0) + (totals.penalty || 0), money: true },
      { label: t('report.net'),          value: totals.net || 0, money: true },
      { label: t('report.employerCost'), value: employerCost, money: true },
    ],
    signature: {
      left:  `${t('report.date')}: ______________`,
      right: t('report.authorizedSignature'),
    },
    footer: [company.CompanyName || company.name, title, period, t('report.internalDoc')].filter(Boolean).join(' · '),
    sheetName: title,
    fileBase,
    orientation: 'landscape',
  };
}

export const exportPayrollExcel = (L) => exportSheetExcel(L);
export const exportPayrollPdf   = (L) => downloadSheetPdf(L);
export const printPayroll       = (L) => printSheet(L);
